import { useEffect, useRef, useState } from 'react';
import { searchSymptoms, type SymptomTag } from '../api.js';

interface Props {
  /** Canonical symptom names already added. */
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}

/** Case-insensitive membership so "Cough" and "cough" don't both stick. */
function has(values: string[], name: string): boolean {
  const lower = name.toLowerCase();
  return values.some((v) => v.toLowerCase() === lower);
}

/**
 * Multi-add symptom picker: a chip row plus a text input whose suggestions come
 * from the shared vocabulary (`GET /symptoms`). Lookups are debounced ~250ms so
 * we never fire a request per keystroke; Enter (or comma) commits the free-typed
 * value, letting the server canonicalize or create it. Removing a chip is a
 * click on it. Keyboard-usable: the input keeps focus, suggestions are buttons.
 */
export default function SymptomInput({ values, onChange, placeholder }: Props): JSX.Element {
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState<SymptomTag[]>([]);
  const [open, setOpen] = useState(false);

  // Debounced vocabulary lookup. The timer is the debounce; `alive` guards the
  // async resolve against a newer keystroke having superseded this one.
  useEffect(() => {
    let alive = true;
    const handle = setTimeout(() => {
      searchSymptoms(input.trim())
        .then((rows) => {
          if (alive) setSuggestions(rows);
        })
        .catch(() => {
          if (alive) setSuggestions([]);
        });
    }, 250);
    return () => {
      alive = false;
      clearTimeout(handle);
    };
  }, [input]);

  function add(name: string): void {
    const trimmed = name.trim();
    if (trimmed.length === 0 || has(values, trimmed)) {
      setInput('');
      return;
    }
    onChange([...values, trimmed]);
    setInput('');
  }

  function remove(name: string): void {
    onChange(values.filter((v) => v !== name));
  }

  const shown = suggestions.filter((s) => !has(values, s.name)).slice(0, 8);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  return (
    <div className="symptominput">
      <div className="chipedit__row">
        {values.map((v) => (
          <button key={v} type="button" className="tagchip tagchip--symptom" onClick={() => remove(v)}>
            {v} <span className="tagchip__x">×</span>
          </button>
        ))}
        <div className="symptominput__field">
          <input
            className="chipedit__input"
            placeholder={placeholder ?? 'add symptom…'}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onFocus={() => setOpen(true)}
            onBlur={() => {
              // Delay so a suggestion mousedown registers before the list hides.
              blurTimer.current = setTimeout(() => setOpen(false), 120);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                add(input);
              } else if (e.key === 'Escape') {
                if (open) {
                  // Close only the suggestion menu; don't let this bubble to
                  // FeelingOffModal's window keydown, which would close the
                  // whole modal and lose whatever the user typed so far.
                  e.stopPropagation();
                  e.preventDefault();
                  setOpen(false);
                }
              }
            }}
          />
          {open && shown.length > 0 && (
            <ul className="symptominput__menu">
              {shown.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    className="symptominput__opt"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      if (blurTimer.current) clearTimeout(blurTimer.current);
                      add(s.name);
                    }}
                  >
                    {s.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
